/**
 * Knowledge sources at or below this display similarity are hidden from the
 * curated "Sources" UI (Outlook/Gmail add-ins, merchant Admin Conversations).
 * Staff All Conversations shows the unfiltered `knowledge_entries_used` audit
 * list instead. Single source of truth for the backend citation builder
 * (apps/aira-rag-backend/src/graph/citations.ts) and any frontend that needs
 * to reproduce the same filter over a differently-shaped list (e.g. merchant
 * Conversations' legacy-row fallback over `knowledge_entries_used`).
 */
export const MIN_SOURCE_SIMILARITY = 0.5;
/**
 * A citation is worth rendering only if it has a header or body text.
 * `toDraftCitations` already guarantees this for anything it emits — call
 * sites keep this check only as defense-in-depth against a citation object
 * built by hand (e.g. a test fixture), so every renderer shares one
 * definition of "blank" instead of three copies of the same expression.
 */
export function hasRenderableText(c) {
    return (c.source || c.text).trim().length > 0;
}
/**
 * Map raw backend run citations into the V1 add-in citation list. Shared by
 * the Gmail BFF and the Outlook adapter so the two platforms keep identical
 * semantics.
 *
 * Backend citations are { chunk_id, intent_index, reason, source?, snippet?,
 * kind?, edit_url? } and repeat the same chunk across intent branches —
 * dedupe by chunk_id. Entries with no source label AND no snippet text are
 * dropped — otherwise the sources sections render blank rows under a
 * misleading "Sources · N" header. kind/edit_url are backend-enriched (live
 * tool sources + admin "Update knowledge" deep link); absent on legacy runs →
 * knowledge row without an edit link.
 */
export function toDraftCitations(raw) {
    const seen = new Set();
    const citations = [];
    for (const c of raw ?? []) {
        if (typeof c === 'string') {
            const t = c.trim();
            if (!t)
                continue;
            citations.push({ id: citations.length + 1, source: '', text: t });
            continue;
        }
        if (!c || typeof c !== 'object')
            continue;
        const o = c;
        const key = String(o.chunk_id ?? `${o.source ?? ''}|${o.snippet ?? o.text ?? ''}`);
        if (seen.has(key))
            continue;
        seen.add(key);
        const sourceLabel = (o.source ?? o.title);
        const text = String(o.snippet ?? o.text ?? o.content ?? '').trim();
        const source = sourceLabel ? String(sourceLabel).trim() : '';
        if (!source && !text)
            continue;
        const kind = o.kind === 'live'
            ? 'live'
            : o.kind === 'knowledge_low_confidence'
                ? 'knowledge_low_confidence'
                : 'knowledge';
        citations.push({
            id: citations.length + 1,
            source,
            text,
            kind,
            ...(typeof o.edit_url === 'string' && o.edit_url ? { edit_url: o.edit_url } : {}),
        });
    }
    return citations;
}
//# sourceMappingURL=citations.js.map